import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import styled from "@emotion/styled";
import {StyledButton} from "../common/StyledButtons";
import {Icon, Intent, Text, Tooltip} from "@blueprintjs/core";
import {Col, GridCell, GridRow, LocationTabButton, LocationTabContainer, Row} from "./styles/styleds";
import AddressInput from "./AddressInput";
import {ClockIcon, InformationIcon, LocationIcon} from "../common/icons";
import {AddressType, LocationTab} from "./types/types";
import {
  RoutePlanningActionType,
  RoutePlanningAsyncActionType,
  getTimeZoneWithCache,
  useRoutePlanningContext,
  useRoutePlanningDispatch
} from "../../providers/RoutePlanningProvider";
import DriverInformationItem from "./DriverInformationItem";
import {COLORS, Constants} from "../../constants/constants";
import TimePicker from "../common/date-time/TimePicker";
import dayjs, {Dayjs} from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {combineDatesAndTime, findEarliestStartDate} from "../../utils/DayjsUtils";
import {useAppContext} from "../../ApplicationContext";
import {DatePickerMui} from "../common/date-time/DatePickerMui";
import StyledNumericInput from "../common/StyledNumericInput";
import {MAX_DWELL_MINUTES, MIN_DWELL_MINUTES} from "../settings/CompanySettings/common/constants";
import {detectWarehouseLocations} from "./DriversInformation.service";
import {DriverLocation, useSearchDriverLocationQuery} from "../../generated/graphql";
import {getGPSStatusType, GPSStatusType} from "../map/driver/DriverMarkerV2";
import axios from "axios";
import {AppToasterTopLeft} from "../../utils/toasters/toaster";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface DriversInformationRef {
  reset: () => void;
}

const DriversInformation = forwardRef<DriversInformationRef>((_, ref) => {
  const {
    appState: {isTimeFormat24hr}
  } = useAppContext();
  const dispatch = useRoutePlanningDispatch();

  const {selectedDrivers, selectedJobs, dwellTime, isSubmitted, submittedAt, currentStep} = useRoutePlanningContext();
  const [activeLocationTab, setActiveLocationTab] = useState<LocationTab>(LocationTab.START);
  const [manifestLocationTab, setManifestLocationTab] = useState<LocationTab>(LocationTab.START);
  const [newDriversLocationTab, setNewDriversLocationTab] = useState<LocationTab>(LocationTab.START);
  const [startLocation, setStartLocation] = useState("");
  const [startLocationType, setStartLocationType] = useState(AddressType.HOME);
  const [endLocation, setEndLocation] = useState("");
  const [endLocationType, setEndLocationType] = useState<AddressType | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [startTimeZone, setStartTimeZone] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // Split drivers into manifest drivers and new drivers
  const manifestDrivers = useMemo(() => selectedDrivers.filter((d) => !!d.manifestDriverId), [selectedDrivers]);
  const newDrivers = useMemo(() => selectedDrivers.filter((d) => !d.manifestDriverId), [selectedDrivers]);

  const [warehouseDetection, setWarehouseDetection] = useState<{
    startWarehouseAddress: string | null;
    endWarehouseAddress: string | null;
  }>({startWarehouseAddress: null, endWarehouseAddress: null});

  // GPS: per-driver current-location state — only for existing manifest drivers
  const [gpsData, setGpsData] = useState<Map<number, {address: string | null; isValid: boolean}>>(new Map());
  // Cache of previous poll's coordinates per driver — avoids re-geocoding when coords haven't changed
  const prevGpsCoordsRef = useRef<Map<number, {coords: string; address: string | null; isValid: boolean}>>(new Map());
  const showTopGps =
    newDrivers.length === 0 &&
    manifestDrivers.length > 0 &&
    manifestDrivers.every((d) => gpsData.get(d.driverId!)?.isValid);

  // Fallback top control from GPS to Home when GPS is no longer available
  useEffect(() => {
    if (!showTopGps && startLocationType === AddressType.GPS) {
      setStartLocationType(AddressType.HOME);
      setStartLocation("");
    }
  }, [showTopGps, startLocationType]);

  const manifestDriverIds = useMemo(
    () => Array.from(new Set(manifestDrivers.map((d) => d.driverId!))),
    [manifestDrivers]
  );
  const driverLocationResult = useSearchDriverLocationQuery({
    skip: currentStep !== 2 || manifestDriverIds.length === 0,
    pollInterval: 15000,
    variables: {
      filter: {driverIds: manifestDriverIds},
      limit: Constants.DRIVER_LOCATION_LIMIT
    }
  });

  useEffect(() => {
    if (driverLocationResult.loading || !driverLocationResult.data) return;
    const items = (driverLocationResult.data.searchDriverLocations?.items ?? []) as DriverLocation[];

    let cancelled = false;
    (async () => {
      const newGpsData = new Map<number, {address: string | null; isValid: boolean}>();

      // Group by driverId — take latest fixDateTime per driver
      const latestByDriver = new Map<number, DriverLocation>();
      for (const loc of items) {
        const existing = latestByDriver.get(loc.driverId);
        if (!existing || new Date(loc.fixDateTime) > new Date(existing.fixDateTime)) {
          latestByDriver.set(loc.driverId, loc);
        }
      }

      // Validate freshness and reverse-geocode, skipping geocode when coordinates are unchanged
      await Promise.all(
        Array.from(latestByDriver.entries()).map(async ([driverId, loc]) => {
          const gpsStatus = getGPSStatusType(loc);
          if (gpsStatus !== GPSStatusType.NEW) {
            newGpsData.set(driverId, {address: null, isValid: false});
            return;
          }
          const coords = loc.coordinates;
          const cached = prevGpsCoordsRef.current.get(driverId);
          if (cached && cached.coords === coords) {
            // Coordinates haven't changed — reuse cached geocode result
            newGpsData.set(driverId, {address: cached.address, isValid: cached.isValid});
            return;
          }
          const [lat, lng] = coords.split(",");
          try {
            const res = await axios.get(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&result_type=street_address&key=${Constants.GOOGLE_API_KEY}`
            );
            if (res.data.results?.length > 0 && res.data.results[0].formatted_address) {
              newGpsData.set(driverId, {address: res.data.results[0].formatted_address, isValid: true});
            } else {
              newGpsData.set(driverId, {address: null, isValid: false});
            }
          } catch {
            newGpsData.set(driverId, {address: null, isValid: false});
          }
        })
      );

      // Update coordinate cache with the results from this poll
      newGpsData.forEach(({address, isValid}, driverId) => {
        const loc = latestByDriver.get(driverId);
        if (loc) {
          prevGpsCoordsRef.current.set(driverId, {coords: loc.coordinates, address, isValid});
        }
      });

      if (!cancelled) setGpsData(newGpsData);
    })();

    return () => {
      cancelled = true;
    };
  }, [driverLocationResult.data, driverLocationResult.loading]);

  // Sync GPS address when validity changes for manifest drivers using GPS type
  useEffect(() => {
    if (gpsData.size === 0) return;
    for (const driver of manifestDrivers) {
      if (driver.startLocationType !== AddressType.GPS) continue;
      const gps = gpsData.get(driver.driverId!);
      if (!gps) continue;
      if (!gps.isValid && driver.startLocation) {
        // GPS went stale → clear address so validation blocks Next
        dispatch({
          type: RoutePlanningActionType.UPDATE_START_LOCATION_FOR_DRIVER,
          payload: {
            driverId: driver.driverId!,
            manifestDriverId: driver.manifestDriverId ?? undefined,
            startLocation: null
          }
        });
      } else if (gps.isValid && gps.address && driver.startLocation !== gps.address) {
        // GPS became valid (or address updated) → fill in the address
        dispatch({
          type: RoutePlanningAsyncActionType.UPDATE_START_LOCATION_FOR_DRIVER_ASYNC,
          payload: {
            driverId: driver.driverId!,
            manifestDriverId: driver.manifestDriverId ?? undefined,
            startLocation: gps.address
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpsData]);

  useEffect(() => {
    let cancelled = false;
    detectWarehouseLocations(selectedJobs).then((result) => {
      if (!cancelled) setWarehouseDetection(result);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedJobs]);

  useEffect(() => {
    if (currentStep !== 2) return;
    if (selectedDrivers.length === 0) return;

    const hasStartWarehouse = !!warehouseDetection.startWarehouseAddress;
    const hasEndWarehouse = !!warehouseDetection.endWarehouseAddress;

    // Update top controls — only skip CUSTOM
    if (startLocationType !== AddressType.CUSTOM) {
      if (hasStartWarehouse && startLocationType !== AddressType.WAREHOUSE) {
        setStartLocationType(AddressType.WAREHOUSE);
        setStartLocation(warehouseDetection.startWarehouseAddress!);
      } else if (!hasStartWarehouse && startLocationType === AddressType.WAREHOUSE) {
        setStartLocationType(AddressType.HOME);
        setStartLocation("");
      }
    }

    if (endLocationType !== AddressType.CUSTOM) {
      if (hasEndWarehouse && endLocationType !== AddressType.WAREHOUSE) {
        setEndLocationType(AddressType.WAREHOUSE);
        setEndLocation(warehouseDetection.endWarehouseAddress!);
      } else if (!hasEndWarehouse && endLocationType === AddressType.WAREHOUSE) {
        setEndLocationType(null);
        setEndLocation("");
      }
    }

    // Pre-fetch timezones once for warehouse addresses, then apply sync to each driver.
    // This avoids N duplicate geocode requests when N drivers all need the same warehouse.
    const applyWarehouse = async () => {
      const startTz = hasStartWarehouse ? await getTimeZoneWithCache(warehouseDetection.startWarehouseAddress) : null;

      for (const driver of selectedDrivers) {
        const driverId = driver.driverId!;
        const manifestDriverId = driver.manifestDriverId ?? undefined;

        if (driver.startLocationType !== AddressType.CUSTOM) {
          if (hasStartWarehouse && driver.startLocationType !== AddressType.WAREHOUSE) {
            dispatch({
              type: RoutePlanningActionType.UPDATE_START_LOCATION_TYPE_FOR_DRIVER,
              payload: {driverId, manifestDriverId, startLocationType: AddressType.WAREHOUSE}
            });
            dispatch({
              type: RoutePlanningActionType.UPDATE_START_LOCATION_FOR_DRIVER,
              payload: {driverId, manifestDriverId, startLocation: warehouseDetection.startWarehouseAddress!}
            });
            if (startTz) {
              dispatch({
                type: RoutePlanningActionType.UPDATE_START_TIME_ZONE_FOR_DRIVER,
                payload: {driverId, manifestDriverId, startTimeZone: startTz}
              });
            }
          } else if (!hasStartWarehouse && driver.startLocationType === AddressType.WAREHOUSE) {
            dispatch({
              type: RoutePlanningAsyncActionType.UPDATE_START_LOCATION_TYPE_FOR_DRIVER_ASYNC,
              payload: {driverId, manifestDriverId, startLocationType: AddressType.HOME}
            });
          }
        }

        if (driver.endLocationType !== AddressType.CUSTOM) {
          if (hasEndWarehouse && driver.endLocationType !== AddressType.WAREHOUSE) {
            dispatch({
              type: RoutePlanningActionType.UPDATE_END_LOCATION_TYPE_FOR_DRIVER,
              payload: {driverId, manifestDriverId, endLocationType: AddressType.WAREHOUSE}
            });
            dispatch({
              type: RoutePlanningActionType.UPDATE_END_LOCATION_FOR_DRIVER,
              payload: {driverId, manifestDriverId, endLocation: warehouseDetection.endWarehouseAddress!}
            });
          } else if (!hasEndWarehouse && driver.endLocationType === AddressType.WAREHOUSE) {
            dispatch({
              type: RoutePlanningAsyncActionType.UPDATE_END_LOCATION_TYPE_FOR_DRIVER_ASYNC,
              payload: {driverId, manifestDriverId, endLocationType: null}
            });
          }
        }
      }
    };
    applyWarehouse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseDetection, dispatch, currentStep, selectedDrivers.length]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      setActiveLocationTab(LocationTab.START);
      setManifestLocationTab(LocationTab.START);
      setNewDriversLocationTab(LocationTab.START);
      setStartLocation("");
      setStartLocationType(AddressType.HOME);
      setEndLocation("");
      setEndLocationType(null);
      setStartDate(null);
      setStartTime(null);
      setStartTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    }
  }));

  useEffect(() => {
    if (selectedJobs.length > 0) {
      const earliestStartDate = findEarliestStartDate(selectedJobs.flatMap((job) => job.stops)).tz(startTimeZone);
      setStartDate(earliestStartDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobs]);

  useEffect(() => {
    const handler = setTimeout(() => {
      const fetchTimeZone = async () => {
        const tz = await getTimeZoneWithCache(startLocation);
        setStartTimeZone(tz);
        if (startDate && !startTime) {
          setStartDate(startDate.tz(tz, true));
        } else if (!startDate && startTime?.isValid()) {
          setStartTime(startTime.tz(tz));
        } else if (startDate?.isValid() && startTime?.isValid()) {
          const combinedDateTime = combineDatesAndTime(startDate, startTime, tz);
          setStartDate(combinedDateTime);
          setStartTime(combinedDateTime);
        }
      };
      fetchTimeZone();
    }, 500);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startLocation]);

  // Auto-switch to tab with validation errors (Start tab has priority)
  useEffect(() => {
    if (isSubmitted) {
      const hasStartError = selectedDrivers.some(
        (driver) => !driver.isStartLocationValid || !driver.isStartDateTimeValid
      );
      const hasEndError = selectedDrivers.some((driver) => !driver.isEndLocationValid);
      if (hasStartError) {
        setManifestLocationTab(LocationTab.START);
        setNewDriversLocationTab(LocationTab.START);
      } else if (hasEndError) {
        setManifestLocationTab(LocationTab.END);
        setNewDriversLocationTab(LocationTab.END);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedAt]);

  // Start Location handlers
  const handleStartLocationChange = useCallback((value: string) => {
    setStartLocation(value);
  }, []);

  const handleStartLocationTypeChange = useCallback(
    (addressType: AddressType | null) => {
      if (addressType === null) return;
      setStartLocationType(addressType);
      if (addressType === AddressType.WAREHOUSE && warehouseDetection.startWarehouseAddress) {
        setStartLocation(warehouseDetection.startWarehouseAddress);
      } else if (addressType === AddressType.GPS) {
        // GPS address varies per driver — the top control just shows the type label
        setStartLocation("");
      } else {
        setStartLocation("");
      }
    },
    [warehouseDetection.startWarehouseAddress]
  );

  const handleApplyStartLocationToAll = useCallback(() => {
    if (startLocationType === AddressType.CUSTOM && !startLocation) {
      return;
    }
    setManifestLocationTab(LocationTab.START);
    setNewDriversLocationTab(LocationTab.START);

    if (startLocationType === AddressType.GPS) {
      // GPS: apply per-driver since each has a different address. Skip invalid GPS.
      let skippedCount = 0;
      for (const driver of selectedDrivers) {
        const gps = gpsData.get(driver.driverId!);
        if (gps?.isValid && gps.address) {
          dispatch({
            type: RoutePlanningAsyncActionType.UPDATE_START_LOCATION_TYPE_FOR_DRIVER_ASYNC,
            payload: {
              driverId: driver.driverId!,
              manifestDriverId: driver.manifestDriverId ?? undefined,
              startLocationType: AddressType.GPS,
              startLocation: gps.address
            }
          });
        } else {
          skippedCount++;
        }
      }
      if (skippedCount > 0) {
        const label = skippedCount === selectedDrivers.length ? "all" : "some";
        AppToasterTopLeft.show({
          message: `Current Location could not be applied to ${label} drivers because their location data is either unavailable or at least 5 minutes old and unreliable.`,
          intent: Intent.WARNING
        });
      }
    } else {
      dispatch({
        type: RoutePlanningAsyncActionType.UPDATE_START_LOCATION_FOR_ALL_DRIVERS_ASYNC,
        payload: {startLocation, startLocationType}
      });
    }
  }, [dispatch, startLocation, startLocationType, selectedDrivers, gpsData]);

  // End Location handlers
  const handleEndLocationChange = useCallback((value: string) => {
    setEndLocation(value);
  }, []);

  const handleEndLocationTypeChange = useCallback(
    (addressType: AddressType | null) => {
      setEndLocationType(addressType);
      if (addressType === AddressType.WAREHOUSE && warehouseDetection.endWarehouseAddress) {
        setEndLocation(warehouseDetection.endWarehouseAddress);
      } else {
        setEndLocation("");
      }
    },
    [warehouseDetection.endWarehouseAddress]
  );

  const handleApplyEndLocationToAll = useCallback(() => {
    if (endLocationType === AddressType.CUSTOM && !endLocation) {
      return;
    }
    setManifestLocationTab(LocationTab.END);
    setNewDriversLocationTab(LocationTab.END);
    dispatch({
      type: RoutePlanningAsyncActionType.UPDATE_END_LOCATION_FOR_ALL_DRIVERS_ASYNC,
      payload: {endLocation, endLocationType: endLocationType}
    });
  }, [dispatch, endLocation, endLocationType]);

  // Apply to all based on active tab
  const handleApplyLocationToAll = useCallback(() => {
    if (activeLocationTab === LocationTab.START) {
      handleApplyStartLocationToAll();
    } else {
      handleApplyEndLocationToAll();
    }
  }, [activeLocationTab, handleApplyStartLocationToAll, handleApplyEndLocationToAll]);

  // Start Date and Time handlers
  const handleStartDateChange = useCallback(
    (value: unknown) => {
      if (startTime?.isValid() && value && (value as Dayjs).isValid()) {
        const combinedDateTime = combineDatesAndTime(value as Dayjs, startTime, startTimeZone);
        setStartTime(combinedDateTime);
        setStartDate(combinedDateTime);
      } else {
        setStartDate(value as any);
      }
    },
    [startTime, startTimeZone]
  );

  const handleStartTimeChange = useCallback(
    (value: unknown) => {
      if (startDate?.isValid() && value && (value as Dayjs).isValid()) {
        const combinedDateTime = combineDatesAndTime(startDate, value as Dayjs, startTimeZone);
        setStartDate(combinedDateTime);
        setStartTime(combinedDateTime);
      } else {
        setStartTime(value as any);
      }
    },
    [startDate, startTimeZone]
  );

  const handleApplyStartDateTimeToAll = () => {
    dispatch({
      type: RoutePlanningActionType.UPDATE_START_DATE_TIME_FOR_ALL_DRIVERS,
      payload: {startDate, startTime, startTimeZone}
    });
  };

  const handleDwellTimeChange = useCallback(
    (value: number | null) => {
      dispatch({
        type: RoutePlanningActionType.UPDATE_DWELL_TIME,
        payload: value || 0
      });
    },
    [dispatch]
  );

  return (
    <OuterContainer>
      <Row gap={24}>
        <Col span={5} gap={4}>
          <Row gap={4}>
            <Icon icon={<LocationIcon />} />
            <LocationTabContainer>
              <LocationTabButton
                isActive={activeLocationTab === LocationTab.START}
                onClick={() => setActiveLocationTab(LocationTab.START)}
                data-testid="location-tab-start"
              >
                Start Location
              </LocationTabButton>
              <LocationTabButton
                isActive={activeLocationTab === LocationTab.END}
                onClick={() => setActiveLocationTab(LocationTab.END)}
                data-testid="location-tab-end"
              >
                End Location
              </LocationTabButton>
            </LocationTabContainer>
          </Row>
          <Row gap={8}>
            <Col>
              {activeLocationTab === LocationTab.START ? (
                <AddressInput
                  address={startLocation}
                  addressType={startLocationType}
                  onAddressChange={handleStartLocationChange}
                  onAddressTypeChange={handleStartLocationTypeChange}
                  showValue={false}
                  dataTestIdPrefix="search-start-location"
                  showWarehouseButton={!!warehouseDetection.startWarehouseAddress}
                  warehouseAddress={warehouseDetection.startWarehouseAddress ?? undefined}
                  showGpsButton={showTopGps}
                />
              ) : (
                <AddressInput
                  address={endLocation}
                  addressType={endLocationType}
                  onAddressChange={handleEndLocationChange}
                  onAddressTypeChange={handleEndLocationTypeChange}
                  showValue={false}
                  dataTestIdPrefix="search-end-location"
                  allowDeselect
                  showStartLocationButton
                  showWarehouseButton={!!warehouseDetection.endWarehouseAddress}
                  warehouseAddress={warehouseDetection.endWarehouseAddress ?? undefined}
                />
              )}
            </Col>
            <Col span={"fit"}>
              <StyledButton
                data-testid="apply-location-to-all-button"
                intent="primary"
                onClick={handleApplyLocationToAll}
              >
                Apply to all
              </StyledButton>
            </Col>
          </Row>
        </Col>
        <Col span={"fit"} gap={4}>
          <Row gap={4}>
            <Col span={"fit"} gap={4}>
              <Row gap={4} style={{height: "28px"}}>
                <Icon icon={"calendar"} color={"#6B7280"} size={16} />
                <Text>Start Date</Text>
              </Row>
              <Row gap={8}>
                <Col style={{width: "140px"}}>
                  <DatePickerMui
                    onChange={handleStartDateChange}
                    value={startDate}
                    timezone={startTimeZone}
                    isValid={true}
                  />
                </Col>
              </Row>
            </Col>
            <Col span={"fit"} gap={4}>
              <Row gap={4} style={{height: "28px"}}>
                <Icon icon={<ClockIcon />} />
                <Text>Start Time</Text>
              </Row>
              <Row gap={8}>
                <Col style={{width: isTimeFormat24hr ? "135px" : "160px"}}>
                  <TimePicker
                    onChange={handleStartTimeChange}
                    value={startTime}
                    isValid={true}
                    showTimeZone={true}
                    timezone={startTimeZone}
                    ampm={!isTimeFormat24hr}
                    referenceDate={startDate as Dayjs}
                    slotProps={{actionBar: {style: {display: "none"}}}}
                  />
                </Col>
              </Row>
            </Col>
            <Col span={"fit"} gap={4}>
              <Row style={{height: "28px"}}></Row>
              <Row>
                <StyledButton intent="primary" onClick={handleApplyStartDateTimeToAll}>
                  Apply to all
                </StyledButton>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={"fit"} gap={4}>
          <Row gap={4} style={{height: "28px"}}>
            <Text>Dwell Time (min)</Text>
            <Tooltip
              content={
                <div>
                  <p style={{margin: "0"}}>Minimum: {MIN_DWELL_MINUTES} minute</p>
                  <p style={{margin: "0"}}>Maximum: {MAX_DWELL_MINUTES} minutes</p>
                </div>
              }
              placement="top"
              popoverClassName="content-tooltip"
              targetProps={{style: {marginTop: "3px"}}}
            >
              <InformationIcon scale={0.7} />
            </Tooltip>
          </Row>
          <Row gap={8}>
            <Col>
              <StyledNumericInput
                data-testid="dwell-time-input"
                value={dwellTime}
                onValueChange={handleDwellTimeChange}
                min={MIN_DWELL_MINUTES}
                max={MAX_DWELL_MINUTES}
                allowNegative={false}
                style={{width: "60px"}}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <TablesContainer hasBoth={manifestDrivers.length > 0 && newDrivers.length > 0}>
        {manifestDrivers.length > 0 && (
          <TableSection>
            <SectionHeader>Existing Manifests</SectionHeader>
            <TableWrapper>
              <GridRow
                gridTemplateColumns={`1.5fr 1.5fr 3fr 150px ${isTimeFormat24hr ? "140px" : "160px"} 80px`}
                style={{backgroundColor: "#F8F8F8", height: "40px"}}
              >
                <GridCell>DRIVER</GridCell>
                <GridCell>DRIVER CODE</GridCell>
                <GridCell>
                  <LocationTabContainer>
                    <LocationTabButton
                      isActive={manifestLocationTab === LocationTab.START}
                      onClick={() => setManifestLocationTab(LocationTab.START)}
                      data-testid="grid-location-tab-start-manifest"
                    >
                      START LOCATION
                    </LocationTabButton>
                    <LocationTabButton
                      isActive={manifestLocationTab === LocationTab.END}
                      onClick={() => setManifestLocationTab(LocationTab.END)}
                      data-testid="grid-location-tab-end-manifest"
                    >
                      END LOCATION
                    </LocationTabButton>
                  </LocationTabContainer>
                </GridCell>
                <GridCell>START DATE</GridCell>
                <GridCell>START TIME</GridCell>
                <GridCell align="center">VEHICLE</GridCell>
              </GridRow>
              <ScrollArea>
                {manifestDrivers.map((driver) => {
                  return (
                    <div key={`manifest-${driver.manifestDriverId}`}>
                      <DriverInformationItem
                        manifestLabel={`Manifest #${driver.manifestDriverId}`}
                        driver={driver}
                        activeLocationTab={manifestLocationTab}
                        showStartWarehouseButton={!!warehouseDetection.startWarehouseAddress}
                        startWarehouseAddress={warehouseDetection.startWarehouseAddress ?? undefined}
                        showEndWarehouseButton={!!warehouseDetection.endWarehouseAddress}
                        endWarehouseAddress={warehouseDetection.endWarehouseAddress ?? undefined}
                        showGpsButton
                        gpsDisabled={!gpsData.get(driver.driverId!)?.isValid}
                        gpsAddress={gpsData.get(driver.driverId!)?.address ?? null}
                        gpsTooltip={
                          gpsData.get(driver.driverId!)?.isValid
                            ? "Current Location"
                            : gpsData.has(driver.driverId!)
                            ? "Driver's location data is at least 5 minutes old and is unreliable"
                            : "Driver's location isn't available"
                        }
                      />
                    </div>
                  );
                })}
              </ScrollArea>
            </TableWrapper>
          </TableSection>
        )}
        {newDrivers.length > 0 && (
          <TableSection>
            {manifestDrivers.length > 0 && <SectionHeader>New Drivers</SectionHeader>}
            <TableWrapper>
              <GridRow
                gridTemplateColumns={`1.5fr 1.5fr 3fr 150px ${isTimeFormat24hr ? "140px" : "160px"} 80px`}
                style={{backgroundColor: "#F8F8F8", height: "40px"}}
              >
                <GridCell>DRIVER</GridCell>
                <GridCell>DRIVER CODE</GridCell>
                <GridCell>
                  <LocationTabContainer>
                    <LocationTabButton
                      isActive={newDriversLocationTab === LocationTab.START}
                      onClick={() => setNewDriversLocationTab(LocationTab.START)}
                      data-testid="grid-location-tab-start"
                    >
                      START LOCATION
                    </LocationTabButton>
                    <LocationTabButton
                      isActive={newDriversLocationTab === LocationTab.END}
                      onClick={() => setNewDriversLocationTab(LocationTab.END)}
                      data-testid="grid-location-tab-end"
                    >
                      END LOCATION
                    </LocationTabButton>
                  </LocationTabContainer>
                </GridCell>
                <GridCell>START DATE</GridCell>
                <GridCell>START TIME</GridCell>
                <GridCell align="center">VEHICLE</GridCell>
              </GridRow>
              <ScrollArea>
                {newDrivers.map((driver) => (
                  <DriverInformationItem
                    key={driver.driverId}
                    driver={driver}
                    activeLocationTab={newDriversLocationTab}
                    showStartWarehouseButton={!!warehouseDetection.startWarehouseAddress}
                    startWarehouseAddress={warehouseDetection.startWarehouseAddress ?? undefined}
                    showEndWarehouseButton={!!warehouseDetection.endWarehouseAddress}
                    endWarehouseAddress={warehouseDetection.endWarehouseAddress ?? undefined}
                  />
                ))}
              </ScrollArea>
            </TableWrapper>
          </TableSection>
        )}
      </TablesContainer>
    </OuterContainer>
  );
});

DriversInformation.displayName = "DriversInformation";

export default DriversInformation;

const OuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 16px;
  overflow: hidden;
`;

const TablesContainer = styled.div<{hasBoth: boolean}>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 16px;
  padding-bottom: 16px;
`;

const TableSection = styled.div`
  flex: 0 1 auto;
  min-height: 150px;
  display: flex;
  flex-direction: column;
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  background-color: ${COLORS.WHITE};
  border-radius: 6px;
  overflow: hidden;
`;

const ScrollArea = styled.div`
  min-height: 0;
  overflow-y: auto;
`;

const SectionHeader = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.TEXT_BLACK};
  padding: 12px 0 6px;
`;
